import { Box, IconButton, Input } from "flicket-ui";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { MagnifyingGlass } from "@phosphor-icons/react";
import debounce from "lodash/debounce";

// TypeScript enhancements with discriminated unions
export type SearchState =
  | { status: "collapsed" }
  | { status: "expanded"; value: string };

export interface CollapsibleSearchProps {
  onChange: (value: string) => void;
  placeholder?: string;
  initialState?: SearchState;
  containerWidth?: {
    collapsed: string | number;
    expanded: string | number;
  };
}

// Constants for configuration
const ANIMATION_DURATION = "0.3s";
const ICON_SIZE = "20px";
const BORDER_RADIUS = "20px";

// Styled components
const SearchContainer = styled(Box)<{ isExpanded: boolean }>`
  position: relative;
  width: ${({ isExpanded }) => (isExpanded ? "300px" : "40px")};
  height: 40px;
  transition: width ${ANIMATION_DURATION} ease;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: ${({ isExpanded }) =>
    isExpanded ? "flex-start" : "center"};
  border-radius: ${BORDER_RADIUS};
  /* background-color: ${({ isExpanded, theme }) =>
    isExpanded ? "white" : theme.colors.N100}; */
  border: ${({ isExpanded, theme }) =>
    isExpanded ? `2px solid ${theme.colors.N800}` : "none"};
  cursor: ${({ isExpanded }) => (isExpanded ? "default" : "pointer")};

  & input,
  & > div,
  & > div > div {
    background: transparent;
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }

  & div[class*="flicket"] {
    border: none !important;
    box-shadow: none !important;
  }
`;

// Styled input to remove all borders
const BorderlessSearchInput = styled(Input)`
  input,
  div {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent;
    padding-left: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-right: 0 !important;
  }

  &:focus-within,
  &:hover,
  &:active {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }

  /* Reduce padding inside the input */
  div {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  /* Remove any container padding */
  margin: 0 !important;

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    padding: 0 !important;
  }
`;

/**
 * A collapsible search component that expands when clicked
 *
 * @param props - Component props
 * @param props.onChange - Callback function when search value changes
 * @param props.placeholder - Placeholder text for search input
 * @param props.initialState - Initial state of the search component
 * @param props.containerWidth - Custom widths for collapsed and expanded states
 */
export const CollapsibleSearch: React.FC<CollapsibleSearchProps> = ({
  onChange,
  placeholder = "Search...",
  initialState = { status: "collapsed" },
}) => {
  // Initialize state based on initialState prop
  const [searchState, setSearchState] = useState<SearchState>(initialState);
  const containerRef = useRef<HTMLDivElement>(null);

  // Derived values
  const isExpanded = searchState.status === "expanded";
  const searchValue = isExpanded ? searchState.value : "";

  /**
   * Toggles between expanded and collapsed states
   */
  const handleToggle = useCallback(() => {
    setSearchState((prev) =>
      prev.status === "collapsed"
        ? { status: "expanded", value: "" }
        : { status: "collapsed" }
    );

    // Clear search value when collapsing
    if (isExpanded) {
      onChange("");
    }
  }, [isExpanded, onChange]);

  /**
   * Updates search value when typing
   */
  const handleChange = useCallback(
    (value: string) => {
      setSearchState({ status: "expanded", value });
      onChange(value);
    },
    [onChange]
  );

  /**
   * Handles clicks outside the search component
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isExpanded &&
        searchValue === ""
      ) {
        setSearchState({ status: "collapsed" });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded, searchValue]);

  return (
    <SearchContainer
      isExpanded={isExpanded}
      onClick={isExpanded ? undefined : handleToggle}
      ref={containerRef}
      aria-expanded={isExpanded}
      role="search"
      aria-label="Search"
      data-testid="collapsible-search"
    >
      <IconButton
        variant="clear"
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
        data-testid="search-icon-container"
        disabled={isExpanded}
        display={{ _: isExpanded ? "none" : "flex", md: "flex" }}
      >
        <MagnifyingGlass />
      </IconButton>

      {isExpanded && (
        <Box flex="1" pl={0} pr={2}>
          <BorderlessSearchInput
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              debounce(() => handleChange(e.target.value), 100)();
            }}
            placeholder={placeholder}
            onBlur={searchValue ? undefined : handleToggle}
            autoFocus={true}
          />
        </Box>
      )}
    </SearchContainer>
  );
};
